import { type HubConnection, HubConnectionState } from '@microsoft/signalr';
import { create } from 'zustand';

/**
 * Realtime Store - Quản lý SignalR connection
 * KHÔNG persist - connection phải được tạo mới mỗi session
 */

interface RealtimeState {
  connection: HubConnection | null;
  connectionState: HubConnectionState;
  subscribedConversations: Set<string>;
  reconnectAttempts: number;
  isReconnecting: boolean;
}

interface RealtimeActions {
  // Connection management
  setConnection: (connection: HubConnection) => void;
  setConnectionState: (state: HubConnectionState) => void;
  disconnect: () => Promise<void>;

  // Subscription management
  subscribeToConversation: (conversationId: string) => Promise<void>;
  unsubscribeFromConversation: (conversationId: string) => Promise<void>;
  unsubscribeAll: () => Promise<void>;

  // Reconnection
  incrementReconnectAttempts: () => void;
  resetReconnectAttempts: () => void;
  setReconnecting: (isReconnecting: boolean) => void;

  // Helpers
  isConnected: () => boolean;
  getReconnectDelay: () => number;
}

type RealtimeStore = RealtimeState & RealtimeActions;

const MAX_RECONNECT_DELAY = 30000; // 30 seconds
const BASE_DELAY = 1000; // 1 second

export const useRealtimeStore = create<RealtimeStore>((set, get) => ({
  // State
  connection: null,
  connectionState: HubConnectionState.Disconnected,
  subscribedConversations: new Set(),
  reconnectAttempts: 0,
  isReconnecting: false,

  // Connection management
  setConnection: (connection) => {
    if (!connection) return;
    set({
      connection,
      connectionState: connection.state,
      reconnectAttempts: 0
    });
  },

  setConnectionState: (state) => {
    set({ connectionState: state });
  },

  disconnect: async () => {
    const { connection, subscribedConversations } = get();

    if (connection && connection.state !== HubConnectionState.Disconnected) {
      try {
        console.info('[Realtime] Disconnecting from SignalR');

        // Unsubscribe from all conversations first
        for (const convId of subscribedConversations) {
          try {
            await connection.invoke('UnsubscribeFromConversation', convId);
          } catch (error) {
            console.warn('[Realtime] Failed to unsubscribe from:', convId, error);
          }
        }

        await connection.stop();
        console.info('[Realtime] Disconnected successfully');
      } catch (error) {
        console.error('[Realtime] Disconnect error:', error);
      } finally {
        set({
          connection: null,
          connectionState: HubConnectionState.Disconnected,
          subscribedConversations: new Set(),
          reconnectAttempts: 0,
          isReconnecting: false
        });
      }
    }
  },

  // Subscription management
  subscribeToConversation: async (conversationId) => {
    const { connection } = get();

    if (!connection || connection.state !== HubConnectionState.Connected) {
      console.warn('[Realtime] Cannot subscribe - not connected');
      return;
    }

    try {
      console.info('[Realtime] Subscribing to conversation:', conversationId);
      await connection.invoke('SubscribeToConversation', conversationId);

      set((state) => ({
        subscribedConversations: new Set(state.subscribedConversations).add(conversationId)
      }));

      console.info('[Realtime] Subscribed successfully:', conversationId);
    } catch (error) {
      console.error('[Realtime] Subscribe error:', error);
    }
  },

  unsubscribeFromConversation: async (conversationId) => {
    const { connection, subscribedConversations } = get();

    if (!connection || connection.state !== HubConnectionState.Connected) {
      // Just remove from local set if not connected
      set((state) => {
        const newSet = new Set(state.subscribedConversations);
        newSet.delete(conversationId);
        return { subscribedConversations: newSet };
      });
      return;
    }

    if (!subscribedConversations.has(conversationId)) {
      return;
    }

    try {
      console.info('[Realtime] Unsubscribing from conversation:', conversationId);
      await connection.invoke('UnsubscribeFromConversation', conversationId);

      set((state) => {
        const newSet = new Set(state.subscribedConversations);
        newSet.delete(conversationId);
        return { subscribedConversations: newSet };
      });

      console.info('[Realtime] Unsubscribed successfully:', conversationId);
    } catch (error) {
      console.error('[Realtime] Unsubscribe error:', error);
    }
  },

  unsubscribeAll: async () => {
    const { subscribedConversations } = get();
    const promises = Array.from(subscribedConversations).map((convId) => get().unsubscribeFromConversation(convId));
    await Promise.allSettled(promises);
  },

  // Reconnection
  incrementReconnectAttempts: () => {
    set((state) => ({
      reconnectAttempts: state.reconnectAttempts + 1
    }));
  },

  resetReconnectAttempts: () => {
    set({ reconnectAttempts: 0 });
  },

  setReconnecting: (isReconnecting) => {
    set({ isReconnecting });
  },

  // Helpers
  isConnected: () => {
    const { connection } = get();
    return connection?.state === HubConnectionState.Connected;
  },

  getReconnectDelay: () => {
    const { reconnectAttempts } = get();
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (max)
    return Math.min(BASE_DELAY * 2 ** reconnectAttempts, MAX_RECONNECT_DELAY);
  }
}));
